// src/components/ApproachSection.jsx
import React, { useState } from "react";
import { createMemoryRouter, useNavigate } from "react-router-dom";
import digitalpen from "../../assets/products/digital-pen.jpg";
import crm from "../../assets/products/crm.jpg"
import erp from "../../assets/products/erp.jpg"

const tabData = [
  {
    key: "crm",
    title: "CampBee CRM",
    shortDescription:
      "CampBee is an all-in-one CRM designed for digital marketers and business owners to automate outreach and turn cold leads into loyal customers-all from one dashboard.",
    points: [
      {
        heading: "Bulk Messaging",
        text: "Reach thousands of prospects instantly with high deliverability.",
      },
      {
        heading: "Smart Auto-Reply",
        text: "Never keep a customer waiting; set instant triggers for common queries.",
      },
      {
        heading: "Custom Chatbots:",
        text: "Build automated flows to qualify leads before you even jump in.",
      },
      {
        heading: "Meta Integration:",
        text: "Sync your Meta Lead Forms directly to CampBee. The moment a lead clicks, your automation begins.",
      },

      {
        heading: "Integrated Cold Calling:",
        text: "A built-in dialer to manage your outbound sales team directly within the CRM.",
      },
    ],
    imageSrc:crm
  },
  {
    key: "erp",
    title: "CampBee ERP",
    shortDescription:
      "Our ERP is designed to simplify complex operations, providing a user-friendly interface that turns messy data into actionable insights. While CampBee ERP is built for versatility, it is uniquely optimized to meet the high demands of the Education Industry.",
    points: [
      {
        heading: "Student Lifecycle Management:",
        text: "From enrollment and digital documentation to alumni tracking.",
      },
      {
        heading: "Academic Resource Planning:",
        text: "Effortless scheduling, faculty management, and curriculum tracking.",
      },
      {
        heading: "Automated Fee Management:",
        text: "Transparent billing, online payments, and automated reminders.",
      },
      {
        heading: "Performance Analytics:",
        text: "Real-time dashboards for student grades, attendance, and institutional growth.",
      },
      {
        heading: "Scalable Architecture:",
        text: "Modular tools that grow alongside your business, whether you are a startup or an established firm.",
      },
    ],
    imageSrc: erp
  },
  {
    key: "campeePen",
    title: "Digital Pen",
    shortDescription:
      "Born from a real-world challenge in higher education, the CampBee Digital Pen is designed to bridge the gap between traditional handwriting and digital efficiency.   No tablets, no expensive screens-just natural writing that digitizes in real-time.",
    points: [
      {
        heading: "Surface Independent:",
        text: " Write on any desk, paper, or flat surface. Our advanced sensors recognize text and mathematical notation through precise hand movement tracking.",
      },
      {
        heading: "Digital Transformation",
        text: "Helping businesses adapt to modern technology trends to stay competitive in a fast-evolving digital landscape.",
      },
      {
        heading: "The Paper-Pen Feel:",
        text: " Maintains the cognitive benefits of handwriting, ensuring that exam results reflect a student’s knowledge, not their typing speed.",
      },
      {
        heading: "Performance Analytics:",
        text: "Real-time dashboards for student grades, attendance, and institutional growth.",
      },
    ],
    imageSrc: digitalpen
  },
];

const ApproachSection = () => {
  const [activeTab, setActiveTab] = useState(tabData[0].key);
  const activeContent =
    tabData.find((tab) => tab.key === activeTab) || tabData[0];

  const navigate = useNavigate();

  return (
    <section className="relative bg-[#f9fafb] font-sans overflow-hidden">
      {/* Hero Banner */}
      <div className="relative w-full min-h-[500px] md:min-h-[550px] lg:min-h-[600px] overflow-hidden rounded-none md:rounded-xl max-w-7xl mx-auto mt-10 flex items-center">
        <img
          src="https://images.pexels.com/photos/4427431/pexels-photo-4427431.jpeg"
          alt="Crack Developers Team"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-16 md:py-20 lg:py-24 text-center md:text-left">
          <span className="text-sm font-medium text-white bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full inline-block mb-4">
            Our Products
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white leading-snug">
            Innovative, scalable, and tailored solutions for every client
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-200 mt-4 max-w-3xl mx-auto md:mx-0">
            Crack Developers is a service and product-based company delivering
            web and software development, AI tools, and strategic consulting
            services. We empower businesses, startups, and educational
            institutes to achieve digital excellence.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="relative -mt-10 sm:-mt-16 md:-mt-20 z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-6 sm:p-10 rounded-2xl shadow-xl border border-gray-100">
          {/* Tabs */}
          <div className="flex justify-between flex-wrap border-b border-gray-200 overflow-x-auto">
            {tabData.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-6 text-base sm:text-lg font-medium transition-all duration-300 ${
                  activeTab === tab.key
                    ? "text-gray-900 border-b-2 border-gray-900"
                    : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-8">
            {/* Left Side */}
            <div className="lg:col-span-2">
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                {activeContent.shortDescription}
              </p>

              <div className="mt-8 space-y-8">
                {activeContent.points.map((point, index) => (
                  <div
                    key={index}
                    className="border-l-2 border-gray-200 pl-6 hover:border-gray-400 transition"
                  >
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-900 font-serif">
                      {point.heading}
                    </h4>
                    <p className="text-gray-600 mt-2 text-sm sm:text-base">
                      {point.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side Image + CTA */}
            <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-md">
              <img
                src={activeContent.imageSrc}
                alt={activeContent.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 w-[90%]">
                <button
                  onClick={() => {
                    navigate("/contact");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="flex items-center justify-center space-x-2 w-full py-3 sm:py-4 bg-white text-black text-sm sm:text-base font-medium rounded-lg hover:bg-black hover:border-white hover:text-white hover:border-2 transition-all duration-300"
                >
                  <span>Book a free consultation</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    ></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;
